<template>
  <PortalWidget :widget="widget">
    <template v-slot:headerContent>
      <div class="header-extra">
        <input
          v-model="filters.name.value"
          type="search"
          :placeholder="$t('PLACEHOLDER_ADDRESS_SEARCH')"
          class="form-control input-md widget-header-input m-r-10"
        >
      </div>
    </template>

    <!-- Action area -->
    <div
      v-if="!loading"
      class="m-t-10 m-b-15"
    >
      <!-- Create Button -->
      <button
        type="button"
        class="btn btn-success btn-sm"
        :disabled="disabled"
        @click="displayCreateForm"
      >
        <i class="fa fa-plus" />
        <span v-t="'ACTION_ADD_ADDRESS'" />
      </button>
    </div>

    <!-- Table Container -->
    <div
      v-if="!loading"
      class="grid_table_holder"
    >
      <v-table
        :data="formattedRowData"
        :current-page.sync="currentPage"
        :page-size="20"
        :filters="filters"
        :hide-sort-icons="true"
        class="table_beauty"
        @totalPagesChanged="totalPages = $event"
      >
        <thead slot="head">
          <tr>
            <th v-if="isMobile" />
            <v-th
              v-for="(col, index) in userVisibleColumns"
              :key="col"
              v-t="columnSettings[col].displayName"
              :class="columnSettings[col].class"
              :sort-key="columnSettings[col].stSort"
            />
            <th
              v-if="!isMobile"
              v-t="'TABLE_COL_ACTIONS'"
            />
          </tr>
        </thead>
        <tbody
          slot="body"
          slot-scope="{ displayData }"
        >
          <!-- Create New Form -->
          <tr
            v-if="showCreateForm"
            class="row-inline-new-form slide-down bg-row-even"
          >
            <td colspan="100">
              <AddressForm
                :show-loader="createAddressStatus.status === 'pending'
                  || updateAddressStatus.status === 'pending'"
                :is-new-address="true"
                @cancel="hideCreateForm"
                @submit="onCreate"
              />
            </td>
          </tr>

          <!-- Rows -->

          <template v-for="(row, index) in displayData">
            <tr
              v-if="!(isMobile && activeActionRowId == row.id)"
              :key="row.id"
              :class="{'bg-row-odd': index % 2 === 0, 'bg-row-even': index % 2 !== 0 }"
            >
              <!-- Mobile Actions -->
              <td v-if="isMobile">
                <button
                  class="btn btn-list-function btn-expand-actions"
                  :disabled="showCreateForm"
                  @click="displayMobileAction(row.id)"
                >
                  <i class="fa fa-ellipsis-v" />
                </button>
              </td>

              <!-- Name -->
              <td
                v-if="userVisibleColumns.includes('name')"
                :class="{clickable: !showCreateForm}"
                @click="!showCreateForm"
              >
                {{ row.name }}
              </td>

              <!-- Country -->
              <td v-if="userVisibleColumns.includes('countryCode')">
                <div>
                  <span
                    class="flag"
                    :class="row.countryCode.toLowerCase()"
                  />
                  {{ row.countryName }}
                </div>
              </td>

              <!-- Address -->
              <td v-if="userVisibleColumns.includes('addressCombined')">
                {{ row.addressCombined }}
              </td>

              <!-- Business -->
              <td v-if="userVisibleColumns.includes('businessName')">
                {{ row.businessName }}
              </td>

              <!-- Desktop Actions -->
              <td v-if="!isMobile">
                <AddressActions
                  :current-address="addresses[index]"
                  :disabled="disabled"
                  @edit="displayEditForm"
                  @confirm-delete="onDeleteConfirmed"
                />
              </td>
            </tr>



            <!-- Edit form -->
            <tr
              v-if="showEditForm(row.id)"
              :key="row.id + '-edit'"
              class="row-inline-new-form slide-down bg-row-even"
            >
              <td colspan="100">
                <AddressForm
                  :show-loader="showFormLoader"
                  :current-address="addresses[index]"
                  :is-new-address="false"
                  @cancel="hideEditForm"
                  @submit="onEdit"
                />
              </td>
            </tr>


            <tr
              v-if="isMobile && activeActionRowId == row.id"
              :key="row.id + '-mobile'"
              class="mobile-actions ng-animate-disabled slide-right"
            >
              <td colspan="10">
                <AddressActions
                  :current-address="row"
                  :disabled="disabled"
                  @collapse-actions="!disabled && hideMobileAction()"
                  @edit="displayEditForm"
                  @confirm-delete="onDeleteConfirmed"
                />
              </td>
            </tr>
          </template>

          <!-- Empty Row -->
          <tr v-if="emptyTable">
            <td
              v-t="'ADDRESS_TABLE_EMPTY'"
              colspan="100%"
            />
          </tr>
        </tbody>
      </v-table>
      <div class="pagination-min">
        <div class="text-center p-7">
          <smart-pagination
            :current-page.sync="currentPage"
            :total-pages="totalPages"
            :max-page-links="20"
          />
        </div>
      </div>
    </div>

    <div
      v-if="loading"
      class="text-center empty-records"
    >
      <Loader />
      <span v-t="'ADDRESS_TABLE_LOADING'" />
    </div>
  </PortalWidget>
</template>

<script>
import PortalWidget from '@/core/components/PortalWidget.vue';
import AddressActions from './AddressActions.vue';
import AddressForm from './AddressForm.vue';
import messages from '../locales';
import Loader from '@/core/components/Loader';
import sharedMessages from '@/core/locales';
import { isMobile } from '@/core/utilities';
import { addressService } from '@/angularBridgeCode/ringbaPortal.servicesModule';
import { utilities } from '@/angularBridgeCode/ringbaPortal.servicesModule';
import { mapActions, mapState, mapGetters } from 'vuex';
import { STATUS_ERROR, STATUS_SUCCESS, STATUS_PENDING } from '@/store/helpers';
import * as actionTypes from '@/modules/addresses/store/actionTypes';

export default {
  name: 'Addresses',
  components: {
    Loader,
    AddressForm,
    AddressActions,
    PortalWidget
  },
  data: () => ({
    currentPage: 1,
    totalPages: 0,
    showCreateForm: false,
    isMobile: isMobile,
    activeActionRowId: null,
    activeEditRowId: null,
    widget: null,
    filters: {
      name: { value: '', keys: ['name','mapToTagType','mapToTagName'] }
    }
  }),

  computed: {
    ...mapState('addresses', [
      'addresses',
      'userVisibleColumns'
    ]),

    ...mapGetters('addresses', [
      'getAddressesStatus',
      'createAddressStatus',
      'updateAddressStatus',
      'deleteAddressStatus',
      'getUserVisibleColumnsStatus',
      'formattedRowData'
    ]),
    ...mapState('ui', [
      'userSettings',
    ]),
    ...mapGetters('ui', [
      'getUserSettingsStatus'
    ]),
    /** @type {() => any} */
    loading() {
      return this.getAddressesStatus.status == STATUS_PENDING;
    },


    /** @type {() => boolean} */
    showFormLoader() {
      return this.createAddressStatus.status === 'pending'
                    || this.updateAddressStatus.status === 'pending';
    },

    /** @type {() => any} */
    emptyTable() {
      return this.getAddressesStatus.status == STATUS_SUCCESS
      && this.safeAddresses?.length == 0;
    },
    /** @type {() => any} */
    safeAddresses() {
      if(this.getAddressesStatus.status == STATUS_SUCCESS){
        return this.addresses;
      }
      return [];
    },
    /** @type {() => any} */
    columnSettings() {
      return addressService.columnSettings;
    },
    /** @type {() => any} */
    disabled() {
      return this.createAddressStatus.status == STATUS_PENDING
      || this.updateAddressStatus.status == STATUS_PENDING
      || this.showCreateForm
      || !!this.activeEditRowId;
    }
  },
  beforeMount() {
    this.widget = {
      title:this.$i18n.t('ADDRESS_HEADER'),
      iconClass: 'typcn typcn-home-outline'
    };
  },
  created() {
    this.getAddresses();
  },
  methods: {
    ...mapActions('addresses', Object.values(actionTypes)),
    /** @type {() => void} */
    updateUserVisibleColumns() {
      this.getUserVisibleColumns();
    },
    /** @type {() => void} */
    displayCreateForm() {
      this.showCreateForm = true;
    },
    /** @type {() => void} */
    hideCreateForm() {
      this.showCreateForm = false;
    },
    /** @type {() => void} */
    displayEditForm(id) {
      this.activeEditRowId = id;
    },
    /** @type {() => void} */
    hideEditForm() {
      this.activeEditRowId = null;
    },
    /** @type {(rowId: string) => boolean} */
    showEditForm(rowId) {
      return !this.showCreateForm && !this.showEditRowId && this.activeEditRowId == rowId;
    },
    /** @type {() => void} */
    displayMobileAction(rowId) {
      this.activeActionRowId = rowId;
    },
    /** @type {() => void} */
    hideMobileAction() {
      this.activeActionRowId = null;
    },
    /** @type {() => void} */
    async onCreate(address) {
      await this.createAddress(address);
      if (this.createAddressStatus.status === STATUS_SUCCESS) {
        utilities.showToaster(STATUS_SUCCESS, this.$i18n.t('MESSAGE_CREATE_SUCCESS'));
        this.hideCreateForm();
      }
      if (this.createAddressStatus.status === STATUS_ERROR) {
        utilities.showToaster(STATUS_ERROR, this.createAddressStatus.error);
      }
    },
    /** @type {() => void} */
    async onEdit(address) {
      await this.updateAddress(address);
      if (this.updateAddressStatus.status === STATUS_SUCCESS) {
        utilities.showToaster(STATUS_SUCCESS, this.$i18n.t('MESSAGE_UPDATE_SUCCESS'));
        this.hideEditForm();
      }
      if (this.updateAddressStatus.status === STATUS_ERROR) {
        utilities.showToaster(STATUS_ERROR, this.updateAddressStatus.error);
      }
    },
    /** @type {() => void} */
    async onDeleteConfirmed(id) {
      await this.deleteAddress(id);
      if (this.deleteAddressStatus.status === STATUS_SUCCESS) {
        utilities.showToaster(STATUS_SUCCESS, this.$i18n.t('MESSAGE_DELETE_SUCCESS'));
      }
      if (this.deleteAddressStatus.status === STATUS_ERROR) {
        utilities.showToaster(STATUS_ERROR, this.deleteAddressStatus.error);
      }
    }
  },
  i18n: {
    messages,
    sharedMessages,
    locale: 'en',
  },
};
</script>
